import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { differenceInCalendarDays } from "date-fns";
import candidatesData from "../../data/candidates.json";
import { useApplicationsByVacancyIdKanban } from "../../hooks/queries/useApplication";
import {
  AlertCircle,
  EqualNot,
  GalleryVerticalEnd,
  Lock,
  LockOpen,
  Mail,
  Pause,
  PauseCircle,
  Search,
  SettingsIcon,
  XIcon,
} from "lucide-react";
import {
  useUpdateApplicationsStatus,
  useToggleApplicationHold,
} from "@/hooks/mutations/applications";
import { Editor } from "@tinymce/tinymce-react";
import Dropdown from "@/components/ui/Dropdown";
// create email
import { useCreateVacancyStatusSettings } from "@/hooks/mutations/vacancyStatusSettings";
// get vacancy status by vacancy id
import { useVacancyStatusSettingsByVacancyId } from "@/hooks/queries/useVacancyStatusSettings";
import ApplicationView from "@/components/applications/ApplicationView";
import ApplicationStatusHistory from "@/components/applications/ApplicationStatusHistory";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import ApplicationViewModal from "@/components/applications/ApplicationViewModal";
import { isLightColor } from "@/utils/helpers";
import ToolTip from "@/components/Common/ToolTip";
// import ApplicationHistoryView from '@/components/applications/ApplicationHistoryView';
// service for manual fetching (used for infinite scroll)
import { applicationService } from "@/services/applicationService";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
// Add this custom Tooltip component at the top of the file
const Tooltip = ({ children, title, multiline = false, align = "center" }) => {
  const positionClass =
    align === "right"
      ? "right-0 left-auto"
      : align === "left"
        ? "left-0 right-auto"
        : "left-1/2 -translate-x-1/2";

  return (
    <div className="relative group">
      {children}
      <div
        className={`absolute bottom-full mb-2 px-4 py-2.5 bg-foreground text-background border border-border text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none shadow-lg leading-5 ${positionClass} ${
          multiline
            ? "w-max max-w-[min(320px,calc(100vw-2rem))]"
            : "whitespace-nowrap px-3 py-1"
        }`}
      >
        {title}
      </div>
    </div>
  );
};

const getColumnLockData = (statusSummary, columnApps = []) => {
  const hasApiLockData =
    Array.isArray(statusSummary?.locked_by_user) ||
    typeof statusSummary?.unlocked_count === "number";

  let lockedByUser = statusSummary?.locked_by_user || [];
  let unlockedCount =
    typeof statusSummary?.unlocked_count === "number"
      ? statusSummary.unlocked_count
      : 0;

  // Fallback from currently loaded column apps if API has no lock summary yet
  if (!hasApiLockData && columnApps.length > 0) {
    const byUser = {};
    let unlocked = 0;
    columnApps.forEach((app) => {
      if (app.is_locked && app.assigned_user_id) {
        const key = app.assigned_user_id;
        if (!byUser[key]) {
          byUser[key] = {
            user_id: key,
            user_name:
              app.assigned_user?.name ||
              app.assignedUser?.name ||
              app.locked_by_user?.[0]?.name ||
              `User #${key}`,
            count: 0,
          };
        }
        byUser[key].count += 1;
      } else {
        unlocked += 1;
      }
    });
    lockedByUser = Object.values(byUser);
    unlockedCount = unlocked;
  }

  const lockedCount = lockedByUser.reduce(
    (total, entry) =>
      total + (entry.count ?? entry.application_ids?.length ?? 0),
    0,
  );

  return {
    lockedByUser,
    unlockedCount,
    hasLockedApplications: lockedCount > 0,
  };
};

const buildColumnLockTooltip = (statusSummary, columnApps = []) => {
  const { lockedByUser, unlockedCount } = getColumnLockData(
    statusSummary,
    columnApps,
  );

  return (
    <div className="min-w-[140px]">
      <div className="text-center font-semibold mb-1.5 pb-1 border-b border-white/20">
        Applications locked
      </div>
      <div className="text-left space-y-0.5">
        {lockedByUser.length > 0 ? (
          lockedByUser.map((entry) => (
            <div key={entry.user_id || entry.user_name}>
              {entry.user_name || entry.name || "Unknown"} ={" "}
              {entry.count ?? entry.application_ids?.length ?? 0}
            </div>
          ))
        ) : (
          <div>—</div>
        )}
        <div>OPEN = {unlockedCount}</div>
      </div>
    </div>
  );
};
const { showToast, hideToast } = useToastStore;
// Strict mode fix
const StrictModeDroppable = ({ children, ...props } = {}) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

const ApplicationKanban = ({ selectedVacancy, selectedStatuses }) => {
  const [applicationsByColumn, setApplicationsByColumn] = useState({});
  const [movementHistory, setMovementHistory] = useState([]);
  const [kanbanChanges, setKanbanChanges] = useState([]);
  const [notification, setNotification] = useState(null);
  const [changeCount, setChangeCount] = useState(0);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pausedApplication, setPausedApplication] = useState(null);
  const [showSettings, setShowSettings] = useState();
  const { showDataLossWarning, setDataLossWarning } = useGlobalStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [showEmailEditor, setShowEmailEditor] = useState(() => {
    const initialValues = {};
    if (selectedVacancy?.vacancy_statuses) {
      selectedVacancy.vacancy_statuses.forEach((status) => {
        initialValues[status.status.id] = true; // Default to true for all statuses
      });
    }
    return initialValues;
  });

  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: itemsPerPage,
  });
  // infinite scroll helpers
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // optional ref if we ever need container specific scrolling
  const boardRef = useRef(null);

  const [emailContent, setEmailContent] = useState({});
  const [emailDelay, setEmailDelay] = useState({});
  const [emailUnit, setEmailUnit] = useState({});
  const [deadlineDate, setDeadlineDate] = useState({});
  const [emailSubject, setEmailSubject] = useState({});
  const [selectedVacancyStatus1, setSelectedVacancyStatus1] = useState({});
  const [selectedVacancyStatus2, setSelectedVacancyStatus2] = useState({});
  const [selectedVacancyStatus3, setSelectedVacancyStatus3] = useState({});
  const [extraSettings, setExtraSettings] = useState({});
  const [lockStatusSummary, setLockStatusSummary] = useState([]);
  const navigate = useNavigate();

  const { mutate: createVacancyStatusSettings } =
    useCreateVacancyStatusSettings(selectedVacancy?.id, showSettings?.id);

  const {
    data: applications,
    isLoading,
    isRefetching: isRefetchingApplications,
    refetch: refetchApplications,
  } = useApplicationsByVacancyIdKanban(
    selectedVacancy?.id,
    {
      per_page: itemsPerPage,
      page: 1, // Always fetch page 1 only
      ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
      ...(sortOrder && { sort_by: "name", sort_order: sortOrder }),
    },
    {
      enabled: !!selectedVacancy && serverPagination.current_page === 1, // Only fetch when on page 1
      keepPreviousData: true, // Keep data when refetching
    },
  );

  const [showApplicationViewModal, setShowApplicationViewModal] =
    useState(null);
  const handleApplicationViewModalOpen = (applicant) => {
    applicant.vacancy = selectedVacancy;
    setShowApplicationViewModal(applicant);
  };
  const handleApplicationViewModalClose = () => {
    setShowApplicationViewModal(null);
  };

  const [showApplicationHistoryModal, setShowApplicationHistoryModal] =
    useState(null);
  const handleApplicationHistoryModalOpen = (applicant) => {
    setShowApplicationHistoryModal(applicant);
  };
  const handleApplicationHistoryModalClose = () => {
    setShowApplicationHistoryModal(null);
  };

  // column status id  const { data: vacancyStatusSettings } = useVacancyStatusSettingsByVacancyId(selectedVacancy?.id, selectedVacancy?.vacancy_statuses[0]?.id);
  const { data: vacancyStatusSettings, refetch: refetchVacancyStatusSettings } =
    useVacancyStatusSettingsByVacancyId(
      selectedVacancy?.id,
      showSettings?.id, // Use the status ID from showSettings
    );

  // Load saved extra_settings when vacancyStatusSettings data is available
  useEffect(() => {
    if (vacancyStatusSettings?.data && showSettings?.id) {
      const savedSettings = vacancyStatusSettings.data;
      console.log("Loading saved settings:", savedSettings);

      // Load saved extra_settings if they exist
      if (savedSettings.extra_settings) {
        console.log("Found extra_settings:", savedSettings.extra_settings);
        setExtraSettings((prev) => ({
          ...prev,
          [showSettings.id]: savedSettings.extra_settings,
        }));

        // Also update the dropdown states with saved values
        if (savedSettings.extra_settings.prescreen_setting?.accept?.id) {
          console.log(
            "Loading accept value:",
            savedSettings.extra_settings.prescreen_setting.accept.id,
          );
          setSelectedVacancyStatus1((prev) => ({
            ...prev,
            [showSettings.id]:
              savedSettings.extra_settings.prescreen_setting.accept.id,
          }));
        }
        if (savedSettings.extra_settings?.callbooked_setting?.id) {
          console.log(
            "Loading callbooked  value:",
            savedSettings.extra_settings.callbooked_setting.id,
          );
          setSelectedVacancyStatus3((prev) => ({
            ...prev,
            [showSettings.id]:
              savedSettings.extra_settings.callbooked_setting.id,
          }));
        }
        if (savedSettings.extra_settings.prescreen_setting?.reject?.id) {
          console.log(
            "Loading reject value:",
            savedSettings.extra_settings.prescreen_setting.reject.id,
          );
          setSelectedVacancyStatus2((prev) => ({
            ...prev,
            [showSettings.id]:
              savedSettings.extra_settings.prescreen_setting.reject.id,
          }));
        }
      }
    }
  }, [vacancyStatusSettings, showSettings?.id]);

  const { mutate: updateStatus, isLoading: isUpdating } =
    useUpdateApplicationsStatus({
      onSuccess: () => {
        // After successful update, refetch applications
        refetchApplications();
      },
    });

  const { mutate: toggleHold, isLoading: isTogglingHold } =
    useToggleApplicationHold({
      onSuccess: () => {
        // After successful hold toggle, refetch applications
        refetchApplications();
        setShowPauseModal(false);
        // Reset icon state to show Pause icon when modal is closed
        if (pausedApplication?.id) {
          setIconStates((prev) => ({
            ...prev,
            [pausedApplication.id]: false,
          }));
        }
        setPausedApplication(null);
        showToast({
          message: "Application hold status updated successfully!",
          type: "success",
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
      onError: (error) => {
        showToast({
          message: `Failed to update application hold status: ${error.message}`,
          type: "error",
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
    });

  // Initialize columns based on vacancy statuses (filtered by selectedStatuses)
  const columns = selectedVacancy?.vacancy_statuses
    ?.filter((status) => selectedStatuses?.has(status.id))
    ?.reduce((acc, status) => {
      let bgColor = { backgroundColor: "blue" }; // Default color as an object
      // Apply specific colors based on status name
      if (status.status.name.toLowerCase() === "applied") {
        bgColor = { backgroundColor: "primary" }; // Kernel dark green
      } else if (status.status.name.toLowerCase() === "rejected") {
        bgColor = { backgroundColor: "#DC2626" }; // Red
      } else if (status.status.bg_color) {
        // Use inline styles to set the background color
        bgColor = { backgroundColor: status.status.bg_color }; // Set as an object for inline style
      }

      acc[status.status.id] = {
        id: status.status.id,
        p_order: status.p_order,
        title: status.status.name,
        bgColor: bgColor, // Store the style object
      };
      return acc;
    }, {});
  // sort columns by p_order and return as an array
  // const columns = Object.values(raw_columns).sort((a, b) => a.p_order - b.p_order);
  // console.log("sortedColumns", columns, raw_columns);

  useEffect(() => {
    if (applications && selectedVacancy && !isLoading) {
      const initialColumns = selectedVacancy.vacancy_statuses
        .filter((status) => selectedStatuses?.has(status.id))
        .reduce((acc, status) => {
          acc[status.status.id] = [];
          return acc;
        }, {});

      // If it's page 1, reset the columns, otherwise merge with existing data
      if (applications.pagination.current_page === 1) {
        applications.applications.forEach((app) => {
          const statusId = app.status?.status?.id || "Application Form";
          if (initialColumns[statusId]) {
            const candidate = app.candidate || {};
            initialColumns[statusId].push({
              ...app,
              candidateName: candidate?.name || "Unknown",
              vacancyTitle: selectedVacancy.title,
            });
          }
        });
        setApplicationsByColumn(initialColumns);
      } else {
        setApplicationsByColumn((prevColumns) => {
          const newColumns = { ...prevColumns };
          applications.applications.forEach((app) => {
            const statusId = app.status?.status?.id || "Application Form";
            if (newColumns[statusId]) {
              const candidate = app.candidate || {};
              // Check if application already exists to avoid duplicates
              const exists = newColumns[statusId].some(
                (existingApp) => existingApp.id === app.id,
              );
              if (!exists) {
                newColumns[statusId].push({
                  ...app,
                  candidateName: candidate?.name || "Unknown",
                  vacancyTitle: selectedVacancy.title,
                });
              }
            }
          });
          return newColumns;
        });
      }

      // update pagination details & hasMore
      if (applications.pagination) {
        console.log("applications.pagination", applications.pagination);
        setServerPagination(applications.pagination);
        // setHasMore(applications.pagination.current_page < applications.pagination.last_page);
      }
    } else {
      // Clear applications when no vacancy is selected
      setApplicationsByColumn({});
    }
  }, [selectedVacancy, isLoading, applications, selectedStatuses]); // Added applications and selectedStatuses as dependencies

  // Keep lock tooltip in sync without full page / kanban refresh
  useEffect(() => {
    if (!selectedVacancy?.id) {
      setLockStatusSummary([]);
      return;
    }

    let cancelled = false;

    const fetchLocks = async () => {
      try {
        const data = await applicationService.getApplicationLocks(
          selectedVacancy.id,
        );
        if (!cancelled && Array.isArray(data?.status_summary)) {
          // Always replace so unlocks (empty locked_by_user) clear the tooltip
          setLockStatusSummary(data.status_summary);
        }
      } catch (error) {
        console.error("Failed to refresh application locks:", error);
      }
    };

    fetchLocks();
    const intervalId = setInterval(fetchLocks, 5000);

    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") {
        fetchLocks();
      }
    };
    document.addEventListener("visibilitychange", refreshOnFocus);
    window.addEventListener("focus", fetchLocks);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshOnFocus);
      window.removeEventListener("focus", fetchLocks);
    };
  }, [selectedVacancy?.id]);

  // Seed from kanban response only until first successful locks poll
  useEffect(() => {
    if (
      lockStatusSummary.length === 0 &&
      Array.isArray(applications?.status_summary) &&
      applications.status_summary.some(
        (s) =>
          Array.isArray(s.locked_by_user) ||
          typeof s.unlocked_count === "number",
      )
    ) {
      setLockStatusSummary(applications.status_summary);
    }
  }, [applications?.status_summary, lockStatusSummary.length]);

  useEffect(() => {
    if (kanbanChanges.length !== 0) {
      setDataLossWarning(true);
    } else {
      setDataLossWarning(false);
    }
  }, [kanbanChanges]);

  // Add this useEffect to log the final history

  const onDragEnd = (result, _data) => {
    console.log(result, _data);
    const { source, destination } = result;
    // If dropped outside the list
    if (!destination) return;

    // If dropped in the same place
    if (
      source.droppableId === destination.droppableId &&
      source.index === source.index
    ) {
      return;
    }

    const start = applicationsByColumn[source.droppableId];
    const finish = applicationsByColumn[destination.droppableId];

    // Moving within the same column
    if (source.droppableId === destination.droppableId) {
      const newItems = Array.from(start);
      const [removed] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, removed);

      const newState = {
        ...applicationsByColumn,
        [source.droppableId]: newItems,
      };

      setApplicationsByColumn(newState);
      return;
    }

    // Moving between columns
    const startItems = Array.from(start);
    const [removed] = startItems.splice(source.index, 1);
    const finishItems = Array.from(finish);

    // Update the status of the moved application
    const updatedItem = {
      ...removed,
      status: destination.droppableId,
    };

    // Update count based on movement - FIXED LOGIC
    setChangeCount((prevCount) => {
      const existingIndex = movementHistory.findIndex(
        (entry) => entry.candidateId === updatedItem.id,
      );

      if (existingIndex !== -1) {
        // If returning to initial stage, decrement count
        if (
          movementHistory[existingIndex].initialStage ===
          destination.droppableId
        ) {
          return Math.max(0, prevCount - 1); // Ensure count doesn't go below 0
        } else {
          // If moving to a new stage (not initial and not current), count remains the same
          return prevCount;
        }
      } else {
        // New movement, increment count
        return prevCount + 1;
      }
    });

    // Add movement to history
    setMovementHistory((prev) => {
      const existingIndex = prev.findIndex(
        (entry) => entry.candidateId === updatedItem.id,
      );

      if (existingIndex !== -1) {
        // Check if candidate is returning to their initial stage
        if (prev[existingIndex].initialStage === destination.droppableId) {
          // Remove the entry if returning to initial stage
          const updatedHistory = prev.filter(
            (_, index) => index !== existingIndex,
          );
          return updatedHistory;
        } else {
          // Update existing entry
          const updatedHistory = [...prev];
          updatedHistory[existingIndex] = {
            ...updatedHistory[existingIndex],
            to: destination.droppableId, // Keep ID instead of name
          };
          return updatedHistory;
        }
      } else {
        // Add new entry if candidate doesn't exist in history and not in initial stage
        if (source.droppableId !== destination.droppableId) {
          const newHistory = [
            ...prev,
            {
              candidateId: updatedItem.id,
              candidateName: updatedItem.candidateName,
              initialStage: source.droppableId, // Keep ID
              to: destination.droppableId, // Keep ID
            },
          ];
          return newHistory;
        }
        return prev;
      }
    });

    // Add kanban change notification
    setKanbanChanges((prev) => {
      // Check if we're returning to initial stage
      const existingIndex = movementHistory.findIndex(
        (entry) => entry.candidateId === updatedItem.id,
      );

      if (
        existingIndex !== -1 &&
        movementHistory[existingIndex].initialStage === destination.droppableId
      ) {
        // If returning to initial stage, remove this candidate from changes
        return prev.filter((change) => change.candidateId !== updatedItem.id);
      }

      // Get stage names from columns for notification
      const sourceStageName =
        columns[source.droppableId]?.title || source.droppableId;
      const destinationStageName =
        columns[destination.droppableId]?.title || destination.droppableId;

      // Otherwise add/update the change
      return [
        ...prev,
        {
          candidateId: updatedItem.id,
          candidateName: updatedItem.candidateName,
          from: source.droppableId, // Keep ID
          to: destination.droppableId, // Keep ID
          fromName: sourceStageName, // Add name for display
          toName: destinationStageName, // Add name for display
          timestamp: Date.now(),
        },
      ];
    });

    // Show notification with correct count
    const newCount = (() => {
      const existingIndex = movementHistory.findIndex(
        (entry) => entry.candidateId === updatedItem.id,
      );

      if (
        existingIndex !== -1 &&
        movementHistory[existingIndex].initialStage === destination.droppableId
      ) {
        return Math.max(0, changeCount - 1); // Decrement if returning to initial
      } else if (existingIndex === -1) {
        return changeCount + 1; // Increment for new movement
      }
      return changeCount; // Keep same count for updates
    })();

    // Only set notification if count is greater than 0
    if (newCount > 0) {
      setNotification({
        id: `kanban-${updatedItem.id}-${Date.now()}`,
        type: "kanban",
        title: `Moved ${updatedItem.candidateName} from ${source.droppableId} to ${destination.droppableId}`,
        count: newCount,
      });
    } else {
      // Close notification if count reaches 0
      setNotification(null);
      setKanbanChanges([]); // Clear changes as well
    }

    finishItems.splice(destination.index, 0, updatedItem);

    const newState = {
      ...applicationsByColumn,
      [source.droppableId]: startItems,
      [destination.droppableId]: finishItems,
    };

    setApplicationsByColumn(newState);

    // Here you would typically make an API call to update the status on the server
    // updateApplicationStatus(updatedItem.id, destination.droppableId);
  };

  // Update ChangesModal component
  const ChangesModal = () => {
    if (!showChangesModal) return null;

    return (
      <div
        className="relative z-10"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="fixed inset-0 bg-foreground/40 transition-opacity"
          aria-hidden="true"
        ></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-base font-semibold text-foreground"
                      id="modal-title"
                    >
                      All Changes
                    </h3>
                    <div className="mt-2">
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {kanbanChanges.map((change, index) => (
                          <div
                            key={index}
                            className="p-3 border-b last:border-b-0"
                          >
                            <p className="text-sm">
                              Moved{" "}
                              <span className="font-medium">
                                {change.candidateName}
                              </span>{" "}
                              from
                              <span className="font-medium">
                                {" "}
                                {change.fromName}
                              </span>{" "}
                              to
                              <span className="font-medium">
                                {" "}
                                {change.toName}
                              </span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={() => setShowChangesModal(false)}
                  className="inline-flex w-full justify-center rounded-md bg-[#0c4f88] px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-[#0a4374] sm:ml-3 sm:w-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Pause Modal component
  const PauseModal = () => {
    if (!showPauseModal || !pausedApplication) return null;

    const handleResume = () => {
      // Create payload structure matching the required format
      const payload = createHoldPayload([pausedApplication], false); // false = resume (not hold)

      // Call the hold API to resume the application
      toggleHold(payload);
    };

    return (
      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-2xl mb-8">
        <div className="bg-card rounded-xl shadow-xl p-6 mx-4">
          <div className="flex items-start gap-4">
            <div className="p-2">
              <PauseCircle className="w-6 h-6 text-[#0c4f88]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-lg">
                Application Paused
              </h3>
              <p className="text-muted-foreground text-sm">
                <strong>{pausedApplication.candidateName}</strong> has been put
                on hold. Click to resume processing.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPauseModal(false);
                  setPausedApplication(null);
                  // Reset icon state to show Pause icon when modal is closed
                  if (pausedApplication?.id) {
                    setIconStates((prev) => ({
                      ...prev,
                      [pausedApplication.id]: false,
                    }));
                  }
                }}
                className="px-4 py-2 text-foreground hover:text-muted-foreground transition-colors text-sm font-medium"
              >
                Close
              </button>
              <button
                onClick={handleResume}
                disabled={isTogglingHold}
                className="px-6 py-2 bg-[#0c4f88] text-white rounded-lg hover:bg-[#0a4374] transition-colors font-medium disabled:opacity-75"
              >
                {isTogglingHold ? "Resuming..." : "Resume"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const refetchApplicationsManually = () => {
    // Force refetch applications after successful update
    refetchApplications({ force: true, throwOnError: true, stale: false }).then(
      (data) => {
        let applications = data.data.applications;
        if (applications && selectedVacancy && !isRefetchingApplications) {
          const initialColumns = selectedVacancy.vacancy_statuses.reduce(
            (acc, status) => {
              acc[status.status.id] = [];
              return acc;
            },
            {},
          );

          applications.applications.forEach((app) => {
            const statusId = app.status?.status?.id || "Application Form";
            if (initialColumns[statusId]) {
              const candidate = app.candidate || {};
              initialColumns[statusId].push({
                ...app,
                candidateName: candidate?.name || "Unknown",
                vacancyTitle: selectedVacancy.title,
              });
            }
          });
          setApplicationsByColumn(initialColumns);
        }
      },
    );
  };
  const submitStatusChanges = () => {
    console.log("submitStatusChanges", kanbanChanges);
    // [
    //   {
    //       "candidateId": 29,
    //       "candidateName": "dan",
    //       "from": "12",
    //       "to": "10",
    //       "fromName": "Application Form",
    //       "toName": "Initial Screening",
    //       "timestamp": 1741685133941
    //   },
    //   {
    //       "candidateId": 30,
    //       "candidateName": "harry",
    //       "from": "12",
    //       "to": "237",
    //       "fromName": "Application Form",
    //       "toName": "Applied",
    //       "timestamp": 1741685137721
    //   }
    // ]

    // Map kanban changes to status changes
    const applicationsWithStatusChanges = kanbanChanges.map((change) => {
      const status = selectedVacancy.vacancy_statuses.find(
        (vs) =>
          vs.status.id == parseInt(change.to) || vs.id == parseInt(change.to),
      );

      return {
        id: change.candidateId,
        vacancy_status_id: status ? status.id : change.to,
      };
    });

    // Create payload structure matching prescreen format
    const payload = {
      hold: [], // Kanban doesn't use hold functionality, so always empty array
      applications: applicationsWithStatusChanges,
    };

    console.log("Kanban payload to be sent:", payload);
    updateStatus(payload); //, { onSuccess: () => refetchApplicationsManually() });
    // traverse through the kanbanChanges and update the applicationsByColumn
    // find the candidate in the applicationsByColumn and update the application.application_status_history by  pushing object
    //
  };

  // Update Notification component
  const Notification = () => {
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (notification && notification.count === 0) {
        setNotification(null);
        setKanbanChanges([]);
      }
    }, [notification?.count]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        const candidateCount = kanbanChanges.length;
        const successMessage =
          candidateCount > 1
            ? "All candidate statuses have been updated successfully!"
            : `Candidate status has been updated successfully!`;

        await submitStatusChanges();
        setKanbanChanges([]);
        setChangeCount(0);
        setNotification(null);
        setMovementHistory([]);
        showToast({
          message: successMessage,
          type: "success",
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        // refetch the applications

        refetchApplications({
          force: true,
          throwOnError: true,
          stale: false,
        }).then((data) => {
          let applications = data.data.applications;
          console.log("applications", applications);
          if (applications && selectedVacancy && !isRefetchingApplications) {
            const initialColumns = selectedVacancy.vacancy_statuses.reduce(
              (acc, status) => {
                acc[status.status.id] = [];
                return acc;
              },
              {},
            );

            applications.forEach((app) => {
              const statusId = app.status?.status?.id || "Application Form";
              if (initialColumns[statusId]) {
                const candidate = app.candidate || {};
                initialColumns[statusId].push({
                  ...app,
                  candidateName: candidate?.name || "Unknown",
                  vacancyTitle: selectedVacancy.title,
                });
              }
            });
            //console.log('initialColumns500', initialColumns);
            setApplicationsByColumn(initialColumns);
          }
        });
      } catch (error) {
        showToast({
          message:
            "Oops! We encountered an issue while saving your changes. Please try again.",
          type: "error",
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } finally {
        setIsSaving(false);
      }
    };

    const handleCancel = () => {
      // Restore original positions using movementHistory
      const newApplications = { ...applicationsByColumn };

      movementHistory.forEach((entry) => {
        // Find the candidate in their current column
        const currentColumn = newApplications[entry.to];
        const candidateIndex = currentColumn.findIndex(
          (app) => app.id === entry.candidateId,
        );

        if (candidateIndex !== -1) {
          // Remove from current column
          const [candidate] = currentColumn.splice(candidateIndex, 1);

          // Add back to original column
          newApplications[entry.initialStage].push(candidate);
        }
      });

      // Update state
      setApplicationsByColumn(newApplications);
      setKanbanChanges([]);
      setChangeCount(0);
      setNotification(null);
      setMovementHistory([]);
    };

    if (!notification) return null;

    return (
      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-2xl mb-8">
        <div className="bg-card rounded-xl shadow-xl p-6 mx-4">
          <div className="flex items-start gap-4">
            <div className="p-2">
              <AlertCircle className="w-6 h-6 text-[#0c4f88]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-lg">
                {notification.count} updates pending
              </h3>
              <button
                onClick={() => setShowChangesModal(true)}
                className="text-[#0c4f88] hover:underline text-sm font-medium"
              >
                View all updates
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-foreground hover:text-red-600 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-[#0c4f88] text-white rounded-lg hover:bg-[#0a4374] transition-colors font-medium disabled:opacity-75"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSaveEmail = async (status) => {
    try {
      const payload = {
        vacancy_id: selectedVacancy.id,
        vacancy_status_id: status.id,
        template_data: emailContent[status.id] || "",
        subject: emailSubject[status.id] || "",
        is_email: showEmailEditor[status.id] ? 1 : 0,
        deadline_date: deadlineDate[status.id] || null,
        extra_settings: extraSettings[status.id] || {
          prescreen_setting: {
            accept: { id: "", name: "" },
            reject: { id: "", name: "" },
          },
          callbooked_setting: { id: "", name: "" },
        },
      };

      // Handle date case
      if (emailUnit[status.id] === "date") {
        payload.delay_date = emailDelay[status.id] || null;
        payload.email_delay = null; // Set email_delay to null for date case
        payload.delay_unit = "date"; // Include delay_unit with date
      } else {
        payload.email_delay = emailDelay[status.id] || 0;
        payload.delay_unit = emailUnit[status.id] || "hours";
      }

      const response = await createVacancyStatusSettings(payload, {
        onSuccess: () => {
          console.log("success");
          showToast({
            message: "Email template saved successfully!",
            type: "success",
          });
          handleCloseSettings(); // Close the modal after successful save
        },
        onError: (error) => {
          showToast({
            message: `Failed to save email template: ${error.message}`,
            type: "error",
          });
        },
      });
      if (response) {
        showToast({
          message: "Email template saved successfully!",
          type: "success",
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });

        // Reset state after successful save
        setShowEmailEditor((prev) => ({
          ...prev,
          [status.id]: false,
        }));
        setEmailContent((prev) => ({
          ...prev,
          [status.id]: "",
        }));
        setDeadlineDate((prev) => ({
          ...prev,
          [status.id]: null,
        }));
      }
    } catch (error) {
      // console.error("Error saving email template:", error);
      showToast({
        message: `Failed to save email template: ${error.message}`,
        type: "error",
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleCloseSettings = async () => {
    console.log("Closing settings for status:", showSettings); // Debug log
    await setShowEmailEditor((prev) => {
      console.log("Resetting email editor state:", prev); // Debug log
      return {
        ...prev,
        [showSettings?.id]: false,
      };
    });
    await setEmailContent((prev) => {
      console.log("Resetting email content:", prev); // Debug log
      return {
        ...prev,
        [showSettings?.id]: "",
      };
    });
    setShowSettings(null);
  };

  useEffect(() => {
    const refetchSettings = async () => {
      // console.log('Settings', showSettings);
      const { data: settings } = await refetchVacancyStatusSettings();
      console.log("refetch", settings.data);
      // we will need to update the showEmailEditor state with the new settings
      setShowEmailEditor((prev) => ({
        ...prev,
        [showSettings?.id]: settings.data?.is_email == 1,
      }));
      setEmailContent((prev) => ({
        ...prev,
        [showSettings?.id]: settings.data?.template_data,
      }));
      setEmailSubject((prev) => ({
        ...prev,
        [showSettings?.id]: settings.data?.subject,
      }));

      // Check if email_delay contains a date format (YYYY-MM-DD)
      const isDateFormat =
        settings.data?.email_delay &&
        /^\d{4}-\d{2}-\d{2}/.test(settings.data.email_delay);
      // If email_delay contains a date format, use it for date input and set unit to "date"
      if (isDateFormat) {
        setEmailDelay((prev) => ({
          ...prev,
          [showSettings?.id]: settings.data?.email_delay,
        }));
        setEmailUnit((prev) => ({
          ...prev,
          [showSettings?.id]: "date",
        }));
      } else {
        // Otherwise use regular delay values
        setEmailDelay((prev) => ({
          ...prev,
          [showSettings?.id]:
            settings.data?.delay_unit === "date"
              ? settings.data?.delay_date
              : settings.data?.email_delay,
        }));
        setEmailUnit((prev) => ({
          ...prev,
          [showSettings?.id]: settings.data?.delay_unit,
        }));
      }

      setDeadlineDate((prev) => ({
        ...prev,
        [showSettings?.id]: settings.data?.deadline_date
          ? settings.data.deadline_date.substring(0, 10)
          : "",
      }));
    };
    if (showSettings?.id) {
      refetchSettings();
    }
  }, [showSettings]);

  // Update the settings click handler
  const handleSettingsClick = (status) => {
    setShowSettings(status);
  };

  const handleColumnTitleClick = (status, vacancyStatusId) => {
    console.log(
      "Clicked Status:",
      status,
      "Vacancy Status ID:",
      vacancyStatusId,
    );
    // Open prescreen for any status with vacancy_status_id
    window.open(
      `/vacancies/${selectedVacancy.id}/pre-screen?vacancy_status_id=${vacancyStatusId}`,
      "_blank",
    );
  };

  // Add this useEffect to handle body overflow
  useEffect(() => {
    if (
      showApplicationViewModal ||
      showApplicationHistoryModal ||
      showSettings ||
      showPauseModal
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [
    showApplicationViewModal,
    showApplicationHistoryModal,
    showSettings,
    showPauseModal,
  ]);

  // Function to fetch next page of applications (used for infinite scrolling)

  const loadMoreApplications = async () => {
    if (!hasMore) return;
    if (isFetchingMore || !selectedVacancy) return;

    const nextPage = serverPagination.current_page + 1;

    // Prevent duplicate calls for the same page
    if (nextPage <= serverPagination.current_page) return;

    setIsFetchingMore(true);
    try {
      const response =
        await applicationService.getApplicationsByVacancyIdKanban(
          selectedVacancy.id,
          {
            per_page: itemsPerPage,
            page: nextPage,
            ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
            ...(sortOrder && { sort_by: "id", sort_order: sortOrder }),
          },
        );

      const newApps = response.applications || [];

      // append new apps to columns avoiding duplicates
      if (newApps.length) {
        setApplicationsByColumn((prev) => {
          const updated = { ...prev };
          newApps.forEach((app) => {
            const statusId = app.status?.status?.id || "Application Form";
            if (!updated[statusId]) {
              updated[statusId] = [];
            }
            // Check if application already exists to avoid duplicates
            const exists = updated[statusId].some((item) => item.id === app.id);
            if (!exists) {
              const candidate = app.candidate || {};
              updated[statusId].push({
                ...app,
                candidateName: candidate?.name || "Unknown",
                vacancyTitle: selectedVacancy.title,
              });
            }
          });
          return updated;
        });
      }

      // update pagination
      setServerPagination((prev) => ({
        ...prev,
        current_page: nextPage,
      }));

      // If no new applications are returned, stop pagination
      // if (response?.pagination?.total < Number(itemsPerPage)) {
      if (response?.applications?.length == 0) {
        console.log(
          "No more applications",
          response?.applications?.length,
          Number(itemsPerPage),
        );
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more applications:", error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const [fetchMoreToastId, setFetchMoreToastId] = useState(null);
  const [iconStates, setIconStates] = useState({}); // Track icon state per application

  // Function to toggle icon state for an application
  const toggleIconState = (appId) => {
    setIconStates((prev) => ({
      ...prev,
      [appId]: !prev[appId], // Toggle between true (EqualNot) and false (Pause)
    }));
  };

  // Function to handle pause icon click
  const handlePauseIconClick = (app) => {
    toggleIconState(app.id); // Toggle icon state
    setPausedApplication(app);
    setShowPauseModal(true);
  };

  // Helper function to create hold payload
  const createHoldPayload = (applications, shouldHold = false) => {
    return {
      applications: applications.map((app) => ({
        id: app.id,
        vacancy_status_id: app.status?.status?.id || app.status,
        on_hold: shouldHold ? 1 : 0,
      })),
    };
  };
  useEffect(() => {
    if (isFetchingMore) {
      setFetchMoreToastId(
        showToast({
          message: (
            <Loader
              className="!w-6 !h-6 !text-white"
              containerClassName="!p-0 !text-white"
              loadingText="Loading more applications..."
              bgColor="transparent"
            />
          ),
          type: null,
          options: {
            position: "top-right",
            autoClose: false,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            closeButton: false,
            progress: undefined,
            theme: "dark",
          },
        }),
      );
    } else {
      hideToast(fetchMoreToastId);
      setFetchMoreToastId(null);
    }
  }, [isFetchingMore]);

  // Infinite scroll listener - trigger when reaching the tallest column
  useEffect(() => {
    let timeoutId;

    const handleScroll = () => {
      // Clear existing timeout to prevent multiple calls
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        // Find the tallest column div
        const columns = document.querySelectorAll("[data-rbd-droppable-id]");
        let maxHeight = 0;
        let tallestColumn = null;

        columns.forEach((column) => {
          const height = column.scrollHeight;
          if (height > maxHeight) {
            maxHeight = height;
            tallestColumn = column;
          }
        });

        // Check if we've reached the end of the tallest column
        if (tallestColumn) {
          const columnRect = tallestColumn.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // If the bottom of the tallest column is visible or close to being visible
          if (columnRect.bottom <= windowHeight + 100) {
            loadMoreApplications();
          }
        }
      }, 100); // Small delay to prevent rapid calls
    };

    const bottomReachedScroll = () => {
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const scrollPos = window.scrollY + winHeight;

      // Only if doc is taller than window AND we're at the bottom
      if (docHeight > winHeight && scrollPos >= docHeight) {
        console.log("Bottom reached!");
        // Clear existing timeout to prevent multiple calls
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          loadMoreApplications();
        }, 250);

        // 👉 Trigger your function here
      }
    };

    // window.addEventListener('scroll', handleScroll);
    window.addEventListener("scroll", bottomReachedScroll);
    return () => {
      // window.removeEventListener('scroll', handleScroll);
      window.removeEventListener("scroll", bottomReachedScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isFetchingMore, selectedVacancy]);
  const filtersStorageKey = `filters_${selectedVacancy?.id || "vacancy"}`;
  const activeFiltersStorageKey = `activeFilters_${
    selectedVacancy?.id || "vacancy"
  }`;

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(filtersStorageKey);
    return saved ? JSON.parse(saved) : {};
  });
  // Reset pagination when vacancy changes
  useEffect(() => {
    setServerPagination({
      current_page: 1,
      last_page: 1,
      total: 0,
      per_page: itemsPerPage,
    });
    setHasMore(true);
  }, [selectedVacancy, itemsPerPage]);

  // Debounce search query with 2 second delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset pagination when debounced search or sort parameters change
  useEffect(() => {
    setServerPagination({
      current_page: 1,
      last_page: 1,
      total: 0,
      per_page: itemsPerPage,
    });
    setHasMore(true);
  }, [debouncedSearchQuery, sortOrder]);

  if (!selectedVacancy) {
    return (
      <div className="w-full p-10 text-center">
        <div className="text-muted-foreground mb-4">
          <GalleryVerticalEnd className="h-14 w-14 mx-auto" />
        </div>
        <p className="text-muted-foreground text-lg font-medium">
          Please select a vacancy to view applications board
        </p>
      </div>
    );
  }

  // Only show loader on initial load (page 1), not during pagination
  if (isLoading && serverPagination.current_page === 1)
    return <Loader bgColor="bg-transparent" />;

  return (
    <div className="w-full p-4">
      <div className="flex gap-3 items-center mb-6 select-none">
        <h2 className="text-xl font-semibold">Applications Board</h2>
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <span className="pointer-events-none absolute flex items-center left-3 top-1/2 -translate-y-1/2">
              <Search className="h-4 w-4 text-muted-foreground" />
            </span>
            <input
              type="text"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 h-[38px] border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg focus:outline-none focus:ring-1 focus:ring-ring text-sm"
            />
          </div>

          {/* Sorting Dropdown */}
          <div className="">
            <Dropdown
              options={[
                { value: "", label: "Please select sorting" },
                { value: "asc", label: "Ascending" },
                { value: "desc", label: "Descending" },
              ]}
              value={
                sortOrder
                  ? {
                      value: sortOrder,
                      label: sortOrder === "asc" ? "Ascending" : "Descending",
                    }
                  : ""
              }
              onChange={(selectedOption) => {
                setSortOrder(selectedOption.value);
              }}
              placeholder="Please select sorting"
            />
          </div>
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div
          className="flex  pb-4"
          style={{ minHeight: "calc(100vh - 200px)" }}
        >
          {selectedVacancy.vacancy_statuses
            .filter((status) => selectedStatuses?.has(status.id))
            .sort((a, b) => a.p_order - b.p_order)
            .map((status) => {
              const column = columns[status.status.id];
              // console.log('Status:', status.status.name, 'Vacancy Status ID:', status.id, 'Status ID:', status.status.id);
              if (!column) return null; // Safety check
              return (
                <div
                  key={status.status.id}
                  className="flex flex-col min-w-[350px] bg-muted rounded-lg mr-4"
                >
                  <div
                    className={`p-3 bg-primary-dark rounded-t-lg`}
                    style={column.bgColor}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <h3
                          // onClick={() => handleColumnTitleClick(column.title, status.id)}
                          // className={`font-semibold cursor-pointer transition-opacity duration-300 hover:opacity-80 ${
                          //   isLightColor(column.bgColor.backgroundColor)
                          //     ? "text-foreground"
                          //     : "text-white"
                          // }`}

                          onClick={() => {
                            // Create URL search parameters from current filters
                            const searchParams = new URLSearchParams();

                            // Add each filter as a search parameter (excluding stage to avoid duplicates)
                            Object.entries(filters).forEach(([key, value]) => {
                              if (
                                key !== "stage" &&
                                value !== null &&
                                value !== undefined &&
                                value !== ""
                              ) {
                                // Handle array values (for multi-select filters)
                                if (Array.isArray(value)) {
                                  value.forEach((item) =>
                                    searchParams.append(key, item),
                                  );
                                } else {
                                  searchParams.append(key, value);
                                }
                              }
                            });

                            // Add the specific stage filter for this column (only this one)
                            searchParams.append("stage", status.status.id);

                            // Add search query if it exists
                            if (searchQuery && searchQuery.trim() !== "") {
                              searchParams.append("search", searchQuery.trim());
                            }

                            // Construct the URL with filters
                            const url = `/vacancies/${selectedVacancy.id}/pre-screen?${searchParams.toString()}`;

                            // Open in new tab
                            window.open(url, "_blank");
                          }}
                          className={`font-semibold cursor-pointer transition-opacity duration-300 hover:opacity-80 ${
                            isLightColor(column.bgColor.backgroundColor)
                              ? "text-foreground"
                              : "text-white"
                          }`}
                        >
                          {column.title}
                        </h3>
                        <span
                          className={`bg-card/30 px-2 rounded-full mx-2 ${
                            isLightColor(column.bgColor.backgroundColor)
                              ? "text-foreground"
                              : "text-white"
                          }`}
                        >
                          {/* {applicationsByColumn[status.status.id]?.length || 0} */}
                          {applications?.status_summary?.find(
                            (s) => s.status_id === status.status.id,
                          )?.application_count || 0}
                        </span>

                        {/* Deadline Date */}
                        {status.vacancy_status_setting?.deadline_date && (
                          <Tooltip
                            title={(() => {
                              const today = new Date();
                              const deadline = new Date(
                                status.vacancy_status_setting.deadline_date,
                              );
                              const diffDays = differenceInCalendarDays(
                                deadline,
                                today,
                              );

                              if (diffDays > 0) {
                                return `${diffDays} days left`;
                              } else if (diffDays < 0) {
                                return `${Math.abs(diffDays)} days past `;
                              } else {
                                return "Due today";
                              }
                            })()}
                          >
                            <span
                              className={`text-[11px]  px-2 py-1 rounded-full cursor-pointer bg-card ${
                                differenceInCalendarDays(
                                  new Date(
                                    status.vacancy_status_setting.deadline_date,
                                  ),
                                  new Date(),
                                ) < 0
                                  ? "text-red-600"
                                  : "text-foreground"
                              }`}
                            >
                              {new Date(
                                status.vacancy_status_setting.deadline_date,
                              )
                                .toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                .toLowerCase()}
                              ,{" "}
                              {new Date(
                                status.vacancy_status_setting.deadline_date,
                              ).toLocaleDateString([], {
                                day: "2-digit",
                                month: "2-digit",
                              })}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const columnStatusSummary =
                            lockStatusSummary.find(
                              (s) =>
                                Number(s.status_id) ===
                                Number(status.status.id),
                            ) ||
                            applications?.status_summary?.find(
                              (s) =>
                                Number(s.status_id) ===
                                Number(status.status.id),
                            );
                          const columnApplicationCount =
                            columnStatusSummary?.application_count ??
                            applications?.status_summary?.find(
                              (s) =>
                                Number(s.status_id) ===
                                Number(status.status.id),
                            )?.application_count ??
                            0;

                          if (Number(columnApplicationCount) === 0) {
                            return null;
                          }

                          const columnApps =
                            applicationsByColumn[status.status.id] || [];
                          const { hasLockedApplications } = getColumnLockData(
                            columnStatusSummary,
                            columnApps,
                          );
                          const LockIcon = hasLockedApplications
                            ? Lock
                            : LockOpen;

                          return (
                            <Tooltip
                              multiline
                              align="center"
                              title={buildColumnLockTooltip(
                                columnStatusSummary,
                                columnApps,
                              )}
                            >
                              <button
                                type="button"
                                aria-label={
                                  hasLockedApplications
                                    ? "Applications locked"
                                    : "No applications locked"
                                }
                                className={`hover:text-muted transition-colors ${
                                  isLightColor(column.bgColor.backgroundColor)
                                    ? "text-foreground"
                                    : "text-white"
                                }`}
                              >
                                <LockIcon
                                  className={`h-5 w-5 ${
                                    !hasLockedApplications ? "-scale-x-100" : ""
                                  }`}
                                />
                              </button>
                            </Tooltip>
                          );
                        })()}
                        <button
                          onClick={() => handleSettingsClick(status)}
                          className={`hover:text-muted transition-colors ${
                            isLightColor(column.bgColor.backgroundColor)
                              ? "text-foreground"
                              : "text-white"
                          }`}
                        >
                          <SettingsIcon className="h-5 w-5" />
                        </button>

                        {/* Settings Modal for this column */}
                        {showSettings?.id == status.id && (
                          <div
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 w-full"
                            onMouseDown={(e) => {
                              // Only close if clicking directly on the overlay
                              if (e.target === e.currentTarget) {
                                handleCloseSettings();
                              }
                            }}
                          >
                            <div
                              className="bg-card rounded-lg p-6 w-1/2 max-h-[90vh] overflow-y-auto"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">
                                  Settings for {column.title}
                                </h3>
                                <button
                                  onClick={handleCloseSettings}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <XIcon className="h-5 w-5" />
                                </button>
                              </div>
                              <div className="mb-4">
                                <div className="text-sm font-medium mb-2">
                                  Settings for pre screen
                                </div>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                  <div>
                                    <label className="block text-sm font-medium mb-1">
                                      Accepted (Thumb Up)
                                    </label>
                                    <Dropdown
                                      options={[
                                        {
                                          value: "",
                                          label: "Please select status",
                                        },
                                        ...(
                                          selectedVacancy?.vacancy_statuses ||
                                          []
                                        )
                                          .filter(
                                            (vacancyStatus) =>
                                              vacancyStatus.id !== status.id,
                                          )
                                          .map((vacancyStatus) => ({
                                            value: vacancyStatus.id,
                                            label: vacancyStatus.status.name,
                                          })),
                                      ]}
                                      sortBy={(a, b) => {
                                        // Keep "Please select status" at the top
                                        if (a.value === "") return -1;
                                        if (b.value === "") return 1;
                                        // Sort others by p_order
                                        const statusA =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == a.value,
                                          );
                                        const statusB =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == b.value,
                                          );
                                        const pOrderA = statusA?.p_order || 999;
                                        const pOrderB = statusB?.p_order || 999;
                                        return pOrderA - pOrderB;
                                      }}
                                      value={(() => {
                                        const selectedStatusId =
                                          selectedVacancyStatus1[status.id];
                                        if (!selectedStatusId) return "";
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );
                                        return selectedStatus
                                          ? {
                                              value: selectedStatus.id,
                                              label: selectedStatus.status.name,
                                            }
                                          : "";
                                      })()}
                                      onChange={(selectedOption) => {
                                        const selectedStatusId =
                                          selectedOption.value;
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );

                                        setSelectedVacancyStatus1((prev) => ({
                                          ...prev,
                                          [status.id]: selectedStatusId,
                                        }));

                                        setExtraSettings((prev) => ({
                                          ...prev,
                                          [status.id]: {
                                            prescreen_setting: {
                                              accept: {
                                                id: selectedStatusId,
                                                name:
                                                  selectedStatus?.status
                                                    ?.name || "",
                                              },
                                              reject: prev[status.id]
                                                ?.prescreen_setting?.reject || {
                                                id: "",
                                                name: "",
                                              },
                                            },
                                            callbooked_setting: prev[status.id]
                                              ?.callbooked_setting || {
                                              id: "",
                                              name: "",
                                            },
                                          },
                                        }));
                                      }}
                                      placeholder="Select Status"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">
                                      Call Booked
                                    </label>
                                    <Dropdown
                                      options={[
                                        {
                                          value: "",
                                          label: "Please select status",
                                        },
                                        ...(
                                          selectedVacancy?.vacancy_statuses ||
                                          []
                                        )
                                          .filter(
                                            (vacancyStatus) =>
                                              vacancyStatus.id !== status.id,
                                          )
                                          .map((vacancyStatus) => ({
                                            value: vacancyStatus.id,
                                            label: vacancyStatus.status.name,
                                          })),
                                      ]}
                                      sortBy={(a, b) => {
                                        // Keep "Please select status" at the top
                                        if (a.value === "") return -1;
                                        if (b.value === "") return 1;
                                        // Sort others by p_order
                                        const statusA =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == a.value,
                                          );
                                        const statusB =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == b.value,
                                          );
                                        const pOrderA = statusA?.p_order || 999;
                                        const pOrderB = statusB?.p_order || 999;
                                        return pOrderA - pOrderB;
                                      }}
                                      value={(() => {
                                        const selectedStatusId =
                                          selectedVacancyStatus3[status.id];
                                        if (!selectedStatusId) return "";
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );
                                        return selectedStatus
                                          ? {
                                              value: selectedStatus.id,
                                              label: selectedStatus.status.name,
                                            }
                                          : "";
                                      })()}
                                      onChange={(selectedOption) => {
                                        const selectedStatusId =
                                          selectedOption.value;
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );

                                        setSelectedVacancyStatus3((prev) => ({
                                          ...prev,
                                          [status.id]: selectedStatusId,
                                        }));

                                        setExtraSettings((prev) => ({
                                          ...prev,
                                          [status.id]: {
                                            callbooked_setting: {
                                              id: selectedStatusId,
                                              name:
                                                selectedStatus?.status?.name ||
                                                "",
                                            },
                                            prescreen_setting: {
                                              accept: prev[status.id]
                                                ?.prescreen_setting?.accept || {
                                                id: "",
                                                name: "",
                                              },
                                              reject: prev[status.id]
                                                ?.prescreen_setting?.reject || {
                                                id: "",
                                                name: "",
                                              },
                                            },
                                          },
                                        }));
                                      }}
                                      placeholder="Select Status"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">
                                      Rejected (Thumb Down)
                                    </label>
                                    <Dropdown
                                      options={[
                                        {
                                          value: "",
                                          label: "Please select status",
                                        },
                                        ...(
                                          selectedVacancy?.vacancy_statuses ||
                                          []
                                        )
                                          .filter(
                                            (vacancyStatus) =>
                                              vacancyStatus.id !== status.id,
                                          )
                                          .map((vacancyStatus) => ({
                                            value: vacancyStatus.id,
                                            label: vacancyStatus.status.name,
                                          })),
                                      ]}
                                      sortBy={(a, b) => {
                                        // Keep "Please select status" at the top
                                        if (a.value === "") return -1;
                                        if (b.value === "") return 1;
                                        // Sort others by p_order
                                        const statusA =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == a.value,
                                          );
                                        const statusB =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == b.value,
                                          );
                                        const pOrderA = statusA?.p_order || 999;
                                        const pOrderB = statusB?.p_order || 999;
                                        return pOrderA - pOrderB;
                                      }}
                                      value={(() => {
                                        const selectedStatusId =
                                          selectedVacancyStatus2[status.id];
                                        if (!selectedStatusId) return "";
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );
                                        return selectedStatus
                                          ? {
                                              value: selectedStatus.id,
                                              label: selectedStatus.status.name,
                                            }
                                          : "";
                                      })()}
                                      onChange={(selectedOption) => {
                                        const selectedStatusId =
                                          selectedOption.value;
                                        const selectedStatus =
                                          selectedVacancy?.vacancy_statuses?.find(
                                            (s) => s.id == selectedStatusId,
                                          );

                                        setSelectedVacancyStatus2((prev) => ({
                                          ...prev,
                                          [status.id]: selectedStatusId,
                                        }));

                                        setExtraSettings((prev) => ({
                                          ...prev,
                                          [status.id]: {
                                            prescreen_setting: {
                                              accept: prev[status.id]
                                                ?.prescreen_setting?.accept || {
                                                id: "",
                                                name: "",
                                              },
                                              reject: {
                                                id: selectedStatusId,
                                                name:
                                                  selectedStatus?.status
                                                    ?.name || "",
                                              },
                                            },
                                            callbooked_setting: prev[status.id]
                                              ?.callbooked_setting || {
                                              id: "",
                                              name: "",
                                            },
                                          },
                                        }));
                                      }}
                                      placeholder="Select Status"
                                    />
                                  </div>
                                </div>
                              </div>
                              <hr className="my-4" />
                              <div className="space-y-4">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <label className="relative inline-flex items-center gap-2 cursor-pointer">
                                      <span className="text-sm font-medium">
                                        Send Email (
                                        {showEmailEditor[status.id]
                                          ? "ON"
                                          : "OFF"}
                                        )
                                      </span>
                                      <div className="relative inline-flex  items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="sr-only peer"
                                          checked={
                                            showEmailEditor[status.id] ?? false
                                          }
                                          onChange={(e) => {
                                            console.log(
                                              "status.id",
                                              status.id,
                                              showEmailEditor,
                                            );
                                            setShowEmailEditor((prev) => ({
                                              ...prev,
                                              [status.id]: e.target.checked,
                                            }));
                                          }}
                                        />
                                        <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                                      </div>
                                    </label>
                                  </div>
                                  {/* <div className="flex items-center gap-2">
                                  <label className="text-sm font-medium">
                                    Email Delay:
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    className="w-16 px-2 py-1 border rounded text-sm"
                                    placeholder="0"
                                    value={emailDelay[status.id] || ""}
                                    onChange={(e) =>
                                      setEmailDelay((prev) => ({
                                        ...prev,
                                        [status.id]: e.target.value,
                                      }))
                                    }
                                    disabled={emailUnit[status.id] === "date"}
                                  />
                                  <select
                                    className="px-2 py-1 border rounded text-sm"
                                    value={emailUnit[status.id] || "hours"}
                                    onChange={(e) =>
                                      setEmailUnit((prev) => ({
                                        ...prev,
                                        [status.id]: e.target.value,
                                      }))
                                    }
                                  >
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                    <option value="minutes">Minutes</option>
                                    <option value="date">Date</option>
                                  </select>
                                  
                                  {emailUnit[status.id] === "date" && (
                                    <input
                                      type="date"
                                      className="px-2 py-1 border rounded text-sm ml-2"
                                      value={emailDelay[status.id] || ""}
                                      onChange={(e) =>
                                        setEmailDelay((prev) => ({
                                          ...prev,
                                          [status.id]: e.target.value,
                                        }))
                                      }
                                      
                                    />
                                  )}
                                </div> */}
                                </div>

                                {showEmailEditor[status.id] ? (
                                  <div className="mt-4 justify-between">
                                    {/* align label center */}
                                    <div className="mb-4 flex gap-2 items-center">
                                      <label className="block text-sm font-medium">
                                        Subject:
                                      </label>
                                      {/* reduce input width to 1/3 */}
                                      <input
                                        type="text"
                                        className="mt-1 block w-1/3 px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                        placeholder="Enter email subject"
                                        value={emailSubject[status.id] || ""}
                                        onChange={(e) =>
                                          setEmailSubject((prev) => ({
                                            ...prev,
                                            [status.id]: e.target.value,
                                          }))
                                        }
                                      />
                                      <div className="flex items-center gap-2 ml-auto">
                                        <label className="text-sm font-medium">
                                          Email Delay:
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          className="w-16 px-2 py-1 border rounded text-sm"
                                          placeholder="0"
                                          value={emailDelay[status.id] || ""}
                                          onChange={(e) =>
                                            setEmailDelay((prev) => ({
                                              ...prev,
                                              [status.id]: e.target.value,
                                            }))
                                          }
                                          disabled={
                                            emailUnit[status.id] === "date"
                                          }
                                        />
                                        <select
                                          className="px-2 py-1 border rounded text-sm"
                                          value={
                                            emailUnit[status.id] || "hours"
                                          }
                                          onChange={(e) =>
                                            setEmailUnit((prev) => ({
                                              ...prev,
                                              [status.id]: e.target.value,
                                            }))
                                          }
                                        >
                                          <option value="hours">Hours</option>
                                          <option value="days">Days</option>
                                          <option value="minutes">
                                            Minutes
                                          </option>
                                          <option value="date">Date</option>
                                        </select>

                                        {emailUnit[status.id] === "date" && (
                                          <input
                                            type="date"
                                            className="px-2 py-1 border rounded text-sm ml-2"
                                            value={emailDelay[status.id] || ""}
                                            onChange={(e) =>
                                              setEmailDelay((prev) => ({
                                                ...prev,
                                                [status.id]: e.target.value,
                                              }))
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <Editor
                                      apiKey={
                                        import.meta.env.VITE_TINYMCE_API_KEY
                                      }
                                      value={emailContent[status.id] || ""}
                                      onEditorChange={(content) =>
                                        setEmailContent((prev) => ({
                                          ...prev,
                                          [status.id]: content,
                                        }))
                                      }
                                      init={{
                                        height: 300,
                                        menubar: false,
                                        plugins: [
                                          "advlist",
                                          "autolink",
                                          "lists",
                                          "link",
                                          "image",
                                          "charmap",
                                          "preview",
                                          "anchor",
                                          "searchreplace",
                                          "visualblocks",
                                          "code",
                                          "fullscreen",
                                          "insertdatetime",
                                          "media",
                                          "table",
                                          "code",
                                          "help",
                                          "wordcount",
                                        ],
                                        toolbar:
                                          "undo redo | blocks | " +
                                          "bold italic forecolor | alignleft aligncenter " +
                                          "alignright alignjustify | bullist numlist outdent indent | " +
                                          "removeformat | help",
                                        content_style:
                                          "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="mt-4 p-4 bg-muted rounded-lg text-center text-muted-foreground">
                                    Email editor is currently disabled
                                  </div>
                                )}

                                <div className="mt-4 p-3 bg-muted rounded-lg w-fit border border-border shadow-sm">
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-[#0c4f88]">
                                        Name:
                                      </span>
                                      <span className="text-foreground font-semibold">
                                        &#123;$name&#125;
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-[#0c4f88]">
                                        Job Title:
                                      </span>
                                      <span className="text-foreground font-semibold">
                                        &#123;$job_title&#125;
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-[#0c4f88]">
                                        Company:
                                      </span>
                                      <span className="text-foreground font-semibold">
                                        &#123;$company_name&#125;
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-6 flex justify-end gap-2">
                                <div className="flex items-center gap-2 mr-auto">
                                  <label className="text-sm font-medium">
                                    Deadline:
                                  </label>
                                  <input
                                    type="date"
                                    className="px-2 py-1 border rounded text-sm"
                                    value={deadlineDate[status.id] ?? ""}
                                    onChange={(e) =>
                                      setDeadlineDate((prev) => ({
                                        ...prev,
                                        [status.id]: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                                <button
                                  onClick={handleCloseSettings}
                                  className="px-4 py-2 bg-muted text-foreground rounded hover:bg-input"
                                >
                                  Close
                                </button>
                                <button
                                  onClick={() => handleSaveEmail(status)}
                                  className="px-4 py-2 bg-primary-dark text-white rounded hover:bg-primary-dark hover:scale-105"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <StrictModeDroppable
                    droppableId={status.status.id.toString()}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-2 flex-1 overflow-y-auto ${
                          snapshot.isDraggingOver ? "bg-muted" : ""
                        }`}
                        style={{ maxHeight: "none", height: "auto" }}
                      >
                        {applicationsByColumn[status.status.id]?.map(
                          (app, index) => (
                            <Draggable
                              key={app.id}
                              draggableId={app.id.toString()}
                              index={index}
                              data-rbd-drag-handle-custom-data={JSON.stringify({
                                status: status.status,
                              })}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`bg-card rounded-lg border my-2 border-border shadow-md ${
                                    snapshot.isDragging
                                      ? "shadow-lg ring-2 ring-primary"
                                      : ""
                                  }`}
                                >
                                  <div className="bg-card p-4 rounded-tr-lg rounded-tl-lg ">
                                    <div className="font-medium text-foreground flex justify-between items-center mb-0.5">
                                      <div className="flex items-center gap-2">
                                        <span>{app.candidateName}</span>
                                        {app.on_hold == 1 && (
                                          <ToolTip
                                            className="inline-flex !w-auto"
                                            tooltipsContent="On Hold"
                                            position="right"
                                            children={
                                              <div
                                                onClick={() =>
                                                  handlePauseIconClick(app)
                                                }
                                                className="cursor-pointer select-none"
                                              >
                                                {iconStates[app.id] ? (
                                                  <EqualNot className="transform scaleX(-1) rotate-90 w-5 h-5 bg-yellow-500 text-white p-1 rounded-full" />
                                                ) : (
                                                  <Pause className="w-5 h-5 bg-yellow-500 text-white p-1 rounded-full" />
                                                )}
                                              </div>
                                            }
                                          />
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {(app.email_status?.email_status?.toLowerCase() ===
                                          "pending" ||
                                          app.email_status?.email_status?.toLowerCase() ===
                                            "sent" ||
                                          app.email_status?.email_status?.toLowerCase() ===
                                            "failed") && (
                                          <ToolTip
                                            tooltipsContent={
                                              app.email_status.email_status.toLowerCase() ===
                                              "pending"
                                                ? `Scheduled for ${new Date(app.email_status.when_to_send).toLocaleDateString()} at ${new Date(app.email_status.when_to_send).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                                : app.email_status.email_status
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                  app.email_status.email_status.slice(
                                                    1,
                                                  ) +
                                                  (["sent", "failed"].includes(
                                                    app.email_status.email_status.toLowerCase(),
                                                  )
                                                    ? ` at ${new Date(app.email_status.sent_at).toLocaleDateString()} ${new Date(app.email_status.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                                    : "")
                                            }
                                            children={
                                              <div
                                                className={`flex items-center gap-1 p-1 px-2 rounded-lg ${
                                                  app.email_status?.email_status?.toLowerCase() ===
                                                  "pending"
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : app.email_status?.email_status?.toLowerCase() ===
                                                        "failed"
                                                      ? "bg-red-100 text-red-700"
                                                      : "bg-secondary-soft "
                                                }`}
                                              >
                                                <Mail className="w-3 h-3" />
                                                {app.email_status
                                                  ?.email_status && (
                                                  <span
                                                    className={`text-xs ${
                                                      app.email_status.email_status.toLowerCase() ===
                                                      "pending"
                                                        ? "text-yellow-700"
                                                        : app.email_status.email_status.toLowerCase() ===
                                                            "failed"
                                                          ? "text-red-700"
                                                          : "text-muted-foreground"
                                                    }`}
                                                  >
                                                    {app.email_status.email_status
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                      app.email_status.email_status.slice(
                                                        1,
                                                      )}
                                                  </span>
                                                )}
                                              </div>
                                            }
                                            position="left"
                                            // className="pointer-events-none"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    {/* <div className="flex text-xs text-muted-foreground items-center gap-2">
                                    <span>{app.email}</span>
                                  </div> */}
                                    <div className="text-sm text-muted-foreground hidden">
                                      {app.vacancyTitle}
                                    </div>
                                    <div className="mt-2 flex justify-between items-center">
                                      <span className="text-xs text-muted-foreground">
                                        Last updated:{" "}
                                        {app.updated_at || app.created_at
                                          ? new Date(
                                              app.updated_at || app.created_at,
                                            ).toLocaleString("en-US", {
                                              year: "numeric",
                                              month: "numeric",
                                              day: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: true,
                                            })
                                          : "N/A"}
                                      </span>
                                    </div>
                                    <div className=" flex justify-between items-center">
                                      {(
                                        status.status?.name || ""
                                      ).toLowerCase() === "rejected" &&
                                        app.application_status_history?.length >
                                          1 && (
                                          <span className="text-xs text-muted-foreground">
                                            Last Status:{" "}
                                            {
                                              app.application_status_history[1]
                                                .vacancy_status?.status?.name
                                            }{" "}
                                            on{" "}
                                            {new Date(
                                              app.application_status_history[1]
                                                .created_at,
                                            ).toLocaleDateString()}{" "}
                                            {new Date(
                                              app.application_status_history[1]
                                                .created_at,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </span>
                                        )}
                                    </div>

                                    {app.email_status?.email_status && (
                                      <span className="text-xs text-muted-foreground hidden">
                                        Email:{" "}
                                        {app.email_status.email_status
                                          .charAt(0)
                                          .toUpperCase() +
                                          app.email_status.email_status.slice(
                                            1,
                                          )}
                                        {app.email_status.email_status.toLowerCase() ===
                                          "sent" && (
                                          <>
                                            {" "}
                                            at{" "}
                                            {new Date(
                                              app.email_status.sent_at,
                                            ).toLocaleDateString()}{" "}
                                            {new Date(
                                              app.email_status.sent_at,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <div className=" flex gap-2  justify-end px-4 py-2  bg-muted rounded-b-lg">
                                    <button
                                      onClick={() =>
                                        handleApplicationHistoryModalOpen(app)
                                      }
                                      className="px-2 py-1 text-xs  text-muted-foreground rounded hover:text-foreground"
                                    >
                                      View History
                                    </button>

                                    <a
                                      href={`/vacancies/${selectedVacancy.id}/applications/${app.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-2 py-1 text-xs bg-primary-dark hover:bg-primary-dark  text-white rounded inline-block"
                                    >
                                      View Profile
                                    </a>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ),
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>
                </div>
              );
            })}
        </div>
      </DragDropContext>
      {/* Loading indicator for infinite scroll */}
      {/* {(isFetchingMore || 1) && (
        <div className="w-full">
          <div className="flex justify-center my-4 max-w-[350px] mx-auto sticky bottom-0 left-0 z-50 bg-card/50 backdrop-blur-sm">
            <Loader className="!w-4 !h-4" loadingText="Loading more applications..." bgColor="transparent" />
          </div>
        </div>
      )} */}
      <Notification />
      <ChangesModal />
      <PauseModal />
      <ApplicationViewModal
        showModal={!!showApplicationViewModal}
        onClose={handleApplicationViewModalClose}
        applicant={showApplicationViewModal}
      />
      {showApplicationHistoryModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[99] bg-black bg-opacity-75"
          onClick={handleApplicationHistoryModalClose}
        >
          <div
            className="bg-card rounded-lg shadow-lg p-6 w-[40%] max-md:w-[60%] relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Status History</h3>
            <div className="max-h-[60vh] overflow-y-auto">
              <ApplicationStatusHistory
                applicant={showApplicationHistoryModal}
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleApplicationHistoryModalClose}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationKanban;
